import { 
  collection, 
  addDoc, 
  doc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot, 
  serverTimestamp,
  query,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../firebase';

export type ChatChannel = 'broadcast' | 'handover' | 'escrow_alert' | 'disputes' | 'direct';
export type MessagePriority = 'normal' | 'urgent' | 'handover';

export interface AdminChatMessage {
  id: string;
  senderUid: string;
  senderEmail: string;
  senderName: string;
  senderRole: string;
  senderAvatar?: string;
  text: string;
  channel: ChatChannel;
  priority: MessagePriority;
  recipientUid?: string;
  recipientName?: string;
  pinned?: boolean;
  createdAt?: any;
}

/**
 * Sends an internal admin team message or shift handover note.
 */
export const sendAdminMessage = async (
  msg: Omit<AdminChatMessage, 'id' | 'createdAt'>
): Promise<string> => {
  const messagesCol = collection(db, 'admin_messages');
  const docRef = await addDoc(messagesCol, {
    ...msg,
    pinned: msg.pinned || false,
    createdAt: serverTimestamp(),
  });
  return docRef.id;
};

/**
 * Real-time subscription to admin team messages.
 */
export const subscribeToAdminMessages = (
  channel: ChatChannel | 'all',
  callback: (messages: AdminChatMessage[]) => void
): (() => void) => {
  const messagesCol = collection(db, 'admin_messages');
  const q = query(messagesCol, orderBy('createdAt', 'desc'), limit(100));

  return onSnapshot(
    q,
    (snapshot) => {
      const all: AdminChatMessage[] = snapshot.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<AdminChatMessage, 'id'>),
      }));

      // Filter by channel if not 'all'
      const filtered = channel === 'all' 
        ? all 
        : all.filter((m) => m.channel === channel);

      // Return chronological order (oldest to newest for chat feed)
      callback(filtered.reverse());
    },
    (err) => {
      console.warn('Admin chat subscription error:', err);
      callback([]);
    }
  );
};

/**
 * Toggles pinned status for handover notes.
 */
export const togglePinMessage = async (messageId: string, pinned: boolean): Promise<void> => {
  const msgRef = doc(db, 'admin_messages', messageId);
  await updateDoc(msgRef, { pinned });
};

/**
 * Deletes an admin message.
 */
export const deleteAdminMessage = async (messageId: string): Promise<void> => {
  const msgRef = doc(db, 'admin_messages', messageId);
  await deleteDoc(msgRef);
};
